import { toAppPath } from '@/utils/app-url'
import { API_CONFIG } from '@/config/api.config'

/**
 * Environment-specific configuration fetched from the backend at startup.
 *
 * Vite substitutes `import.meta.env.VITE_*` at build time, which would freeze these
 * values into the artifact — one build per environment, and values that already live
 * in `appsettings.{env}.json` / Key Vault duplicated into the CI configuration. The
 * SPA is served same-origin by the .NET app, so it can instead ask for them at
 * runtime, exactly as the V2 Razor layouts do via `@inject TelemetryConfiguration`.
 *
 * Served by `WebApp/Controllers/V3/ClientConfigController.cs` (anonymous — the public
 * Company Preview page needs it logged out). Contains no secrets.
 */
export interface RuntimeConfig {
  /** FO API origin for the anonymous Company Preview endpoints (DEV-7227). */
  foApiUrl: string
  /** Google reCAPTCHA v3 site key — public by design, rendered into the page. */
  recaptchaSiteKey: string
  /** App Insights connection string for SPA telemetry; empty disables telemetry. */
  appInsightsConnectionString: string
  /** AI natural-language search / LookAlike API origin. */
  aiSearchApiUrl: string
  /**
   * Minutes without activity before the user is signed out: the server's CookieExpireMinutes,
   * the limit the classic (Vue 2) app uses, so both apps sign people out at the same time.
   */
  sessionTimeoutMinutes: number
}

/**
 * Used until the fetch resolves, and permanently if it fails. Everything degrades to
 * a disabled feature rather than a crash: empty `foApiUrl`/`recaptchaSiteKey` put the
 * preview page into its "not configured" branch, and an empty connection string makes
 * telemetry a no-op. The session timeout falls back to the build's value, which matches
 * the server's.
 */
const DEFAULTS: RuntimeConfig = {
  foApiUrl: '',
  recaptchaSiteKey: '',
  appInsightsConnectionString: '',
  aiSearchApiUrl: 'https://api2.tami.ai',
  sessionTimeoutMinutes: API_CONFIG.sessionTimeoutMinutes,
}

const TEXT_KEYS = [
  'foApiUrl',
  'recaptchaSiteKey',
  'appInsightsConnectionString',
  'aiSearchApiUrl',
] as const

let config: RuntimeConfig = { ...DEFAULTS }
let loadPromise: Promise<void> | null = null

/** Ignores absent, blank or invalid values so a partial payload can't wipe out a default. */
function merge(payload: Partial<Record<keyof RuntimeConfig, unknown>>): RuntimeConfig {
  const merged = { ...DEFAULTS }
  for (const key of TEXT_KEYS) {
    const value = payload[key]
    if (typeof value === 'string' && value !== '') {
      merged[key] = value
    }
  }
  const minutes = payload.sessionTimeoutMinutes
  if (typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0) {
    merged.sessionTimeoutMinutes = minutes
  }
  return merged
}

/**
 * Fetch the runtime configuration. Idempotent — concurrent callers share one request.
 * Never rejects: a failure logs and leaves the defaults in place so app bootstrap is
 * never blocked by a config outage.
 *
 * Deliberately uses `fetch` rather than the shared http-client: this call is anonymous
 * and must not pass through the error interceptor, which redirects to login on a 401.
 */
export function loadRuntimeConfig(): Promise<void> {
  loadPromise ??= (async () => {
    try {
      const response = await fetch(toAppPath('/client-config'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      config = merge(await response.json())
    } catch {
      // A failed /client-config fetch is a handled, non-fatal condition: the app boots on the
      // DEFAULTS above (telemetry off, empty FO API / reCAPTCHA, default AI search URL), so the
      // only user-visible effect is the logged-out Company Preview page. Swallow it silently — the
      // browser still logs the failed request itself (that network line cannot be suppressed from
      // JS; only fixing the server or not calling the endpoint removes it).
    }
  })()
  return loadPromise
}

export function getRuntimeConfig(): RuntimeConfig {
  return config
}

/** Test seam — set values without going through the network. */
export function setRuntimeConfig(overrides: Partial<RuntimeConfig>): void {
  config = { ...config, ...overrides }
  loadPromise = Promise.resolve()
}

/** Test seam — restore defaults and allow a fresh load. */
export function resetRuntimeConfig(): void {
  config = { ...DEFAULTS }
  loadPromise = null
}
